import { Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Home, ArrowLeft, Search } from "lucide-react"
import { Helmet } from "react-helmet-async"
import { useAuth } from "@/contexts/AuthContext"
import { FuseguLogo } from "@/components/FuseguLogo"

export default function NotFoundPage() {
  const { user } = useAuth()

  return (
    <>
      <Helmet>
        <title>Page Not Found | Fusegu Security</title>
        <meta name="description" content="The page you're looking for doesn't exist. Return to Fusegu's security scanning platform." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="flex min-h-screen w-full flex-col">
      {/* Header */}
      <header className="sticky top-0 flex h-16 items-center gap-4 border-b bg-background px-4 md:px-6 z-50">
        <div className="flex items-center justify-between w-full">
          <Link to="/" className="flex items-center gap-2 text-lg font-bold">
            <FuseguLogo className="h-6 w-6 flex-shrink-0" />
            <span>Fusegu</span>
          </Link>
          <div className="flex items-center gap-4">
            {user ? (
              <Link to="/dashboard">
                <Button variant="ghost">Dashboard</Button>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost">Sign In</Button>
                </Link>
                <Link to="/register">
                  <Button>Start Free Trial</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* 404 Content */}
      <main className="flex-1 flex items-center justify-center px-4">
        <div className="max-w-2xl w-full text-center">
          <div className="mb-6">
            <h1 className="text-8xl md:text-9xl font-bold text-primary mb-3">404</h1>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              Page Not Found
            </h2>
            <p className="text-base text-gray-600 mb-6">
              The page you're looking for doesn't exist or has been moved.
            </p>
          </div>

          <Card className="border-2">
            <CardContent className="pt-6">
              <div className="grid gap-3 md:grid-cols-3">
                {user ? (
                  <>
                    <Link to="/dashboard" className="block">
                      <Button variant="outline" className="w-full h-auto py-3 flex flex-col gap-1">
                        <Home className="h-5 w-5" />
                        <span className="font-semibold text-sm">Dashboard</span>
                      </Button>
                    </Link>
                    <Link to="/scans" className="block">
                      <Button variant="outline" className="w-full h-auto py-3 flex flex-col gap-1">
                        <Search className="h-5 w-5" />
                        <span className="font-semibold text-sm">Scans</span>
                      </Button>
                    </Link>
                    <Link to="/reports" className="block">
                      <Button variant="outline" className="w-full h-auto py-3 flex flex-col gap-1">
                        <ArrowLeft className="h-5 w-5" />
                        <span className="font-semibold text-sm">Reports</span>
                      </Button>
                    </Link>
                  </>
                ) : (
                  <>
                    <Link to="/" className="block">
                      <Button variant="outline" className="w-full h-auto py-3 flex flex-col gap-1">
                        <Home className="h-5 w-5" />
                        <span className="font-semibold text-sm">Home</span>
                      </Button>
                    </Link>
                    <Link to="/register" className="block">
                      <Button variant="outline" className="w-full h-auto py-3 flex flex-col gap-1">
                        <Search className="h-5 w-5" />
                        <span className="font-semibold text-sm">Get Started</span>
                      </Button>
                    </Link>
                    <Link to="/login" className="block">
                      <Button variant="outline" className="w-full h-auto py-3 flex flex-col gap-1">
                        <ArrowLeft className="h-5 w-5" />
                        <span className="font-semibold text-sm">Sign In</span>
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t py-8 px-4 md:px-8">
        <div className="max-w-6xl mx-auto text-center text-sm text-gray-500">
          <p>© 2025 Fusegu Security. Professional vulnerability assessment platform.</p>
        </div>
      </footer>
      </div>
    </>
  )
}
